import { useCallback, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { spassuCodeApiRef } from '../api';
import { PERMISSION_MATRIX } from '@internal/spassu-code-contract';
import type { AccessibleProject, RbacAction, Role } from '../api/types';

export interface UseProjectRoleResult {
  roles: AccessibleProject[];
  userId: string | undefined;
  loading: boolean;
  error: Error | undefined;
  hasPermission: (projectKey: string, action: RbacAction) => boolean;
  canAccessProject: (projectKey: string) => boolean;
  getRolesForProject: (projectKey: string) => Role[];
  isAdmin: boolean;
  refresh: () => void;
}

export function useProjectRole(): UseProjectRoleResult {
  const api = useApi(spassuCodeApiRef);
  const [refreshKey, setRefreshKey] = useState(0);

  const { value, loading, error } = useAsync(
    () => api.getMyRoles(),
    [refreshKey],
  );

  const hasPermission = useCallback(
    (projectKey: string, action: RbacAction): boolean => {
      if (!value) return false;
      const allowedRoles = PERMISSION_MATRIX[action];
      if (!allowedRoles) return false;

      return value.data.some(
        p =>
          (p.projectKey === projectKey || p.projectKey === '*') &&
          p.roles.some(r => allowedRoles.includes(r)),
      );
    },
    [value],
  );

  const canAccessProject = useCallback(
    (projectKey: string): boolean => {
      if (!value) return false;
      return value.data.some(
        p => p.projectKey === projectKey || p.projectKey === '*',
      );
    },
    [value],
  );

  const getRolesForProject = useCallback(
    (projectKey: string): Role[] => {
      if (!value) return [];
      const allRoles: Role[] = [];
      for (const p of value.data) {
        if (p.projectKey === projectKey || p.projectKey === '*') {
          for (const r of p.roles) {
            if (!allRoles.includes(r)) allRoles.push(r);
          }
        }
      }
      return allRoles;
    },
    [value],
  );

  const isAdmin =
    value?.data.some(p => p.projectKey === '*' && p.roles.includes('admin')) ??
    false;

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  return {
    roles: value?.data ?? [],
    userId: value?.userId,
    loading,
    error,
    hasPermission,
    canAccessProject,
    getRolesForProject,
    isAdmin,
    refresh,
  };
}
